/**
 * CopyButton Component
 *
 * Standardized copy-to-clipboard button with animated state transition.
 * Uses shadcn/ui Button with smooth icon swap animation.
 *
 * @example
 * // Basic usage
 * <CopyButton value="text to copy" />
 *
 * // Icon-only (no text)
 * <CopyButton value={address} iconOnly size="sm" />
 *
 * // Custom labels
 * <CopyButton value={address} label="Copy Address" copiedLabel="Address Copied!" />
 *
 * // With callback
 * <CopyButton value={signature} onCopy={() => toast.success('Copied!')} />
 */

import { useState, useCallback } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** The text value to copy to clipboard */
  value: string;
  /** Label shown before copy (default: "Copy") */
  label?: string;
  /** Label shown after copy (default: "Copied!") */
  copiedLabel?: string;
  /** Show only icon, no text */
  iconOnly?: boolean;
  /** Callback after successful copy */
  onCopy?: () => void;
  /** Duration to show "Copied" state in ms (default: 1500) */
  copiedDuration?: number;
}

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied!',
  iconOnly = false,
  onCopy,
  copiedDuration = 1500,
  variant = 'outline',
  size,
  className,
  disabled,
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!value || copied) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), copiedDuration);
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), copiedDuration);
    }
  }, [value, copied, onCopy, copiedDuration]);

  if (iconOnly) {
    return (
      <Button
        variant={variant}
        size={size || 'icon'}
        className={cn('relative', className)}
        onClick={handleCopy}
        disabled={disabled || copied}
        aria-label={copied ? copiedLabel : label}
        {...props}
      >
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center transition-all duration-200',
            copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
          )}
        >
          <Check className="h-4 w-4 text-green-500" />
        </span>
        <span
          className={cn(
            'transition-all duration-200',
            copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
          )}
        >
          <Copy className="h-4 w-4" />
        </span>
      </Button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={cn('relative disabled:opacity-100', className)}
      onClick={handleCopy}
      disabled={disabled || copied}
      {...props}
    >
      <span
        className={cn(
          'transition-all duration-200',
          copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
        )}
      >
        <Check className="h-4 w-4 text-green-500" />
      </span>
      <span
        className={cn(
          'absolute left-4 transition-all duration-200',
          copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        )}
      >
        <Copy className="h-4 w-4" />
      </span>
      {copied ? copiedLabel : label}
    </Button>
  );
}

export default CopyButton;
